import api from './axiosClient';
import type {
  Ticket, TicketStatus, Account, Client, AccountContact, MessageEvent, InternalNote,
  User, PromptConfig, IntegrationSetting,
  SnapshotItem, TicketSummary, DashboardMetrics, DashboardSummary, PagedResult,
  MarketingDocument, FileUploadResponse,
  Lead, LeadListResponse, ConversionResponse,
  MaterialsCatalogResponse,
  CreateReplyWithMaterialsRequest,
  ReplyWithAttachmentsResponse,
  LeadReply, TicketReply, LeadNote,
  AccountSearchResult,
} from '@/types';
import type { MeetingsListResponse } from '@/types/meeting';
import type { GlobalSearchResponse } from '@/types/search';

// --- Tickets ---
export const getTickets = (params?: { accountId?: string; status?: string; ownerFilter?: 'me' | 'all'; page?: number; pageSize?: number; sortBy?: string | null; sortDir?: 'asc' | 'desc' | null; filter?: Record<string, string> }) => {
  const { filter, ...rest } = params ?? {};
  const filterParams: Record<string, string> = {};
  if (filter) {
    for (const [col, val] of Object.entries(filter)) {
      filterParams[`filter[${col}]`] = val;
    }
  }
  return api.get<PagedResult<Ticket>>('/tickets', { params: { ...rest, ...filterParams } }).then(r => r.data);
};

export const getTicket = (id: string) =>
  api.get<Ticket>(`/tickets/${id}`).then(r => r.data);

export const createTicket = (body: { clientId: string; category: number; priority: number }) =>
  api.post<Ticket>('/tickets', body).then(r => r.data);

export const assignTicket = (ticketId: string, newOwnerUserId: string) =>
  api.post(`/tickets/${ticketId}/assign`, { newOwnerUserId });

export const updateTicketStatus = (ticketId: string, newStatus: TicketStatus) =>
  api.post(`/tickets/${ticketId}/status`, { newStatus });

// --- Clients ---
export const getClient = (id: string) =>
  api.get<Client>(`/clients/${id}`).then(r => r.data);

export const resolveClient = (body: { normalizedEmail?: string; normalizedPhone?: string; displayName?: string }) =>
  api.post<Client>('/clients/resolve', body).then(r => r.data);

// --- Accounts (admin) ---
export const getAccounts = (params?: { status?: string }) =>
  api.get<Account[]>('/admin/accounts', { params }).then(r => r.data);

export const getAccount = (id: string) =>
  api.get<Account>(`/admin/accounts/${id}`).then(r => r.data);

export const createAccount = (body: { companyName?: string; firstName?: string; lastName?: string; email?: string; phone?: string; country: string; city?: string; street?: string }) =>
  api.post<Account>('/admin/accounts', body).then(r => r.data);

export const updateAccount = (id: string, body: { companyName?: string; firstName?: string; lastName?: string; email?: string; phone?: string; country: string; city?: string; street?: string; status?: string }) =>
  api.patch<Account>(`/admin/accounts/${id}`, body).then(r => r.data);

// --- Account Contacts (admin) ---
export const getAccountContacts = (accountId: string, employmentStatus?: string) => {
  const params = employmentStatus ? { employmentStatus } : {};
  return api.get<AccountContact[]>(`/admin/accounts/${accountId}/contacts`, { params }).then(r => r.data);
};

export const getAccountContact = (accountId: string, contactId: string) =>
  api.get<AccountContact>(`/admin/accounts/${accountId}/contacts/${contactId}`).then(r => r.data);

export const createAccountContact = (accountId: string, body: { firstName: string; lastName: string; email: string; phone: string; role?: string; idNumber?: string; dateOfBirth?: string; isEmployed?: boolean; isDefault?: boolean }) =>
  api.post<AccountContact>(`/admin/accounts/${accountId}/contacts`, body).then(r => r.data);

export const updateAccountContact = (accountId: string, contactId: string, body: { firstName?: string; lastName?: string; email?: string; phone?: string; role?: string; idNumber?: string; dateOfBirth?: string; isEmployed?: boolean; status?: string }) =>
  api.patch<AccountContact>(`/admin/accounts/${accountId}/contacts/${contactId}`, body).then(r => r.data);

// --- Messages ---
export const getMessages = (ticketId: string) =>
  api.get<MessageEvent[]>(`/messages/ticket/${ticketId}`).then(r => r.data);

export const createReplyWithMaterials = (
  ticketId: string,
  request: CreateReplyWithMaterialsRequest,
  idempotencyKey: string
): Promise<ReplyWithAttachmentsResponse> =>
  api.post<ReplyWithAttachmentsResponse>(
    `/tickets/${ticketId}/replies`,
    request,
    { headers: { 'X-Idempotency-Key': idempotencyKey } }
  ).then(r => r.data);

// --- Internal Notes ---
export const getNotes = (ticketId: string) =>
  api.get<InternalNote[]>(`/tickets/${ticketId}/internal-notes`).then(r => r.data);

export const createNote = (ticketId: string, content: string, noteType: number = 1) =>
  api.post<InternalNote>(`/tickets/${ticketId}/internal-notes`, { content, noteType }).then(r => r.data);

// --- Users (admin) ---
export const getUsers = () =>
  api.get<User[]>('/admin/users').then(r => r.data);

export const createUser = (body: { email: string; displayName: string; role: number; googleSubjectId: string }) =>
  api.post<User>('/admin/users', body).then(r => r.data);

export const updateUser = (id: string, body: { displayName?: string; role?: string; roles?: string[]; isActive?: boolean }) =>
  api.patch(`/admin/users/${id}`, body);

// --- Prompts (admin) ---
export const getPrompts = () =>
  api.get<PromptConfig[]>('/admin/prompts').then(r => r.data);

export const upsertPrompt = (key: string, body: { key: string; description: string; template: string; parametersJson: string; isActive: boolean }) =>
  api.put<PromptConfig>(`/admin/prompts/${key}`, body).then(r => r.data);

// --- Integrations (admin) ---
export const getIntegrations = () =>
  api.get<IntegrationSetting[]>('/admin/integrations').then(r => r.data);

export const upsertIntegration = (key: string, body: { integrationType: number; key: string; valueRef: string; isEnabled: boolean }) =>
  api.put<IntegrationSetting>(`/admin/integrations/${key}`, body).then(r => r.data);

// --- Email Templates (admin) ---
export const getEmailTemplates = () =>
  api.get('/admin/settings/email-templates').then(r => r.data);

export const updateEmailTemplate = (templateType: string | number, body: { subjectTemplate: string; bodyHtml: string }) =>
  api.put(`/admin/settings/email-templates/${templateType}`, body).then(r => r.data);

// --- Analytics ---
export const getSnapshots = (from: string, to: string, key?: string) =>
  api.get<SnapshotItem[]>('/metrics/snapshots', { params: { from, to, key } }).then(r => r.data);

export const getSummary = () =>
  api.get<TicketSummary>('/metrics/summary').then(r => r.data);

// T113: Dashboard metrics with server-side caching (Feature 022-client-ux-internal-ids)
export const getDashboardMetrics = () =>
  api.get<DashboardMetrics>('/dashboard/metrics').then(r => r.data);

// T009: Live KPI counts for dashboard summary tiles (Feature 034-dashboard-search-filter-owner-fixes)
export const getDashboardSummary = () =>
  api.get<DashboardSummary>('/dashboard/summary').then(r => r.data);

// T017: Global search (Feature 034-dashboard-search-filter-owner-fixes)
export const getSearch = (q: string): Promise<GlobalSearchResponse> =>
  api.get<GlobalSearchResponse>('/search', { params: { q } }).then(r => r.data);

// --- Marketing Materials ---
export const uploadMarketingMaterial = (file: File, headline: string, description: string): Promise<FileUploadResponse> => {
  const form = new FormData();
  form.append('file', file);
  form.append('headline', headline);
  form.append('description', description);
  return api.post<FileUploadResponse>('/marketing-materials/upload', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then(r => r.data);
};

export const getMarketingMaterials = (
  { page = 1, pageSize = 20 }: { page?: number; pageSize?: number } = {},
): Promise<PagedResult<MarketingDocument>> =>
  api.get<PagedResult<MarketingDocument>>('/marketing-materials', { params: { page, pageSize } }).then(r => r.data);

export const getPublicMarketingMaterials = (): Promise<MaterialsCatalogResponse> =>
  api.get<MaterialsCatalogResponse>('/marketing-materials/catalog/public').then(r => r.data);

export const downloadMarketingMaterial = async (id: string, filename: string): Promise<void> => {
  const response = await api.get(`/marketing-materials/${id}`, {
    responseType: 'blob',
  });
  
  // Create blob URL and trigger download
  const responseContentType = response.headers['content-type'];
  const contentType = typeof responseContentType === 'string'
    ? responseContentType
    : 'application/octet-stream';
  const blob = new Blob([response.data], { type: contentType });
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.URL.revokeObjectURL(url);
};

export const deleteMarketingMaterial = (documentId: string): Promise<void> =>
  api.delete(`/marketing-materials/${documentId}`).then(() => undefined);

export const updateMarketingMaterial = (
  documentId: string,
  updates: { headline?: string; description?: string; isPublic?: boolean },
): Promise<MarketingDocument> =>
  api.patch<MarketingDocument>(`/marketing-materials/${documentId}`, updates).then(r => r.data);

// --- Leads ---
export const getLeads = (params?: { 
  status?: number; 
  ownerId?: string;
  ownerFilter?: 'me' | 'all';
  leadSource?: string; 
  pageNumber?: number; 
  pageSize?: number;
  sortBy?: string | null;
  sortDir?: 'asc' | 'desc' | null;
  filter?: Record<string, string>;
}) => {
  const { filter, ...rest } = params ?? {};
  // Serialise filter as filter[col]=val bracket params
  const filterParams: Record<string, string> = {};
  if (filter) {
    for (const [col, val] of Object.entries(filter)) {
      filterParams[`filter[${col}]`] = val;
    }
  }
  return api.get<LeadListResponse>('/leads', { params: { ...rest, ...filterParams } }).then(r => r.data);
};

export const getLead = (id: string) =>
  api.get<Lead>(`/leads/${id}`).then(r => r.data);

export const createLead = (body: { 
  accountId: string; 
  priority?: number; 
  leadSource?: string; 
  ownerUserId?: string;
}) =>
  api.post<Lead>('/leads', body).then(r => r.data);

export const updateLead = (id: string, body: { 
  status?: number; 
  priority?: number; 
  leadSource?: string; 
  ownerUserId?: string;
}) =>
  api.put<Lead>(`/leads/${id}`, body).then(r => r.data);

export const deleteLead = (id: string) =>
  api.delete(`/leads/${id}`).then(() => undefined);

export const convertLeadToTicket = (leadId: string, confirmLostConversion: boolean = false) =>
  api.post<ConversionResponse>(`/leads/${leadId}/convert-to-ticket`, { confirmLostConversion }).then(r => r.data);

export const convertTicketToLead = (ticketId: string) =>
  api.post<ConversionResponse>(`/tickets/${ticketId}/convert-to-lead`).then(r => r.data);

// T029/T031: Updated to PATCH /leads/{id}/assign with new AssignLeadRequest shape (Feature 034)
export const assignLead = (leadId: string, ownerId: string | null) =>
  api.patch(`/leads/${leadId}/assign`, { ownerId });

export const getLeadReplies = (leadId: string) =>
  api.get<LeadReply[]>(`/leads/${leadId}/replies`).then(r => r.data);

export const getTicketReplies = (ticketId: string) =>
  api.get<TicketReply[]>(`/tickets/${ticketId}/replies`).then(r => r.data);

export const createLeadReplyWithMaterials = (
  leadId: string,
  request: CreateReplyWithMaterialsRequest,
  idempotencyKey: string
): Promise<ReplyWithAttachmentsResponse> =>
  api.post<ReplyWithAttachmentsResponse>(
    `/leads/${leadId}/replies`,
    request,
    { headers: { 'X-Idempotency-Key': idempotencyKey } }
  ).then(r => r.data);

export const getLeadNotes = (leadId: string) =>
  api.get<LeadNote[]>(`/leads/${leadId}/internal-notes`).then(r => r.data);

export const createLeadNote = (leadId: string, content: string, noteType: number = 1) =>
  api.post<LeadNote>(`/leads/${leadId}/internal-notes`, { content, noteType }).then(r => r.data);

// --- Meetings ---
export const getMeetings = (params?: { page?: number; pageSize?: number }) =>
  api.get<MeetingsListResponse>('/meetings', { params }).then(r => r.data);

export const getClientMeetings = (accountId: string, params?: { page?: number; pageSize?: number }) =>
  api.get<MeetingsListResponse>('/meetings', { params: { accountId, ...params } }).then(r => r.data);

// --- Account search (US1 Feature 039) ---
/** Search accounts by company name or contact email. Min 2 chars. Returns up to 20 results. */
export const searchAccounts = (q: string): Promise<AccountSearchResult[]> =>
  api.get<AccountSearchResult[]>('/admin/accounts/search', { params: { q } }).then(r => r.data);

// --- Mark-read (US2 Feature 039) ---
/** Mark a lead as read for the current user.
 *  Returns previousLastReadAt — the user's last-read timestamp before this call,
 *  which the caller uses to identify new-since-last-visit messages. */
export const markLeadRead = (id: string): Promise<{ previousLastReadAt: string | null }> =>
  api.patch<{ previousLastReadAt: string | null }>(`/leads/${id}/mark-read`).then(r => r.data);

/** Mark a ticket as read for the current user.
 *  Returns previousLastReadAt — the user's last-read timestamp before this call. */
export const markTicketRead = (id: string): Promise<{ previousLastReadAt: string | null }> =>
  api.patch<{ previousLastReadAt: string | null }>(`/tickets/${id}/mark-read`).then(r => r.data);

// --- Filter options (US5 Feature 039) ---
/** Returns distinct values for a filterable lead column (owner | companyName). */
export const getLeadFilterOptions = (column: string): Promise<string[]> =>
  api.get<string[]>('/leads/filter-options', { params: { column } }).then(r => r.data);

/** Returns distinct values for a filterable ticket column (owner | companyName). */
export const getTicketFilterOptions = (column: string): Promise<string[]> =>
  api.get<string[]>('/tickets/filter-options', { params: { column } }).then(r => r.data);

// --- Catalog (Feature 044) ---
import type {
  CatalogSupplier, CatalogLocation, CatalogCategory, CatalogConcernTag,
  CatalogPackageType, CatalogFragranceType, CatalogBase, CatalogActive, CatalogFragrance,
  CatalogPackaging, CatalogPackagingSet, CatalogGeneratedSku, CatalogLabel, CatalogFile, CatalogSkuHistory, CatalogOrder,
  LabelFileResponse,
  CreateCatalogSupplierData, CreateCatalogLocationData, CreateCatalogCategoryData,
  CreateCatalogConcernTagData, CreateCatalogPackageTypeData, CreateCatalogBaseData,
  CreateCatalogActiveData, CreateCatalogFragranceData, CreateCatalogPackagingData,
  CreateCatalogPackagingSetData,
  UpdateCatalogPackagingSetData,
  CreateCatalogGeneratedSkuData, CreateCatalogLabelData, CreateCatalogFileData, CreateCatalogOrderData,
  UpdateCatalogSupplierData, UpdateCatalogLocationData, UpdateCatalogCategoryData,
  UpdateCatalogConcernTagData, UpdateCatalogPackageTypeData, UpdateCatalogBaseData,
  UpdateCatalogActiveData, UpdateCatalogFragranceData, UpdateCatalogPackagingData,
  UpdateCatalogGeneratedSkuData, UpdateCatalogLabelData, UpdateCatalogFileData, UpdateCatalogOrderData,
  CreateCatalogFragranceTypeData, UpdateCatalogFragranceTypeData,
} from '@/types/catalog';

export const getCatalogSuppliers = (): Promise<CatalogSupplier[]> =>
  api.get<CatalogSupplier[]>('/admin/catalog/suppliers').then(r => r.data);

export const getCatalogLocations = (): Promise<CatalogLocation[]> =>
  api.get<CatalogLocation[]>('/admin/catalog/locations').then(r => r.data);

export const getCatalogCategories = (): Promise<CatalogCategory[]> =>
  api.get<CatalogCategory[]>('/admin/catalog/categories').then(r => r.data);

export const getCatalogConcernTags = (): Promise<CatalogConcernTag[]> =>
  api.get<CatalogConcernTag[]>('/admin/catalog/concern-tags').then(r => r.data);

export const getCatalogPackageTypes = (): Promise<CatalogPackageType[]> =>
  api.get<CatalogPackageType[]>('/admin/catalog/package-types').then(r => r.data);

export const getCatalogBases = (): Promise<CatalogBase[]> =>
  api.get<CatalogBase[]>('/admin/catalog/bases').then(r => r.data);

export const getCatalogActives = (): Promise<CatalogActive[]> =>
  api.get<CatalogActive[]>('/admin/catalog/actives').then(r => r.data);

export const getCatalogFragrances = (): Promise<CatalogFragrance[]> =>
  api.get<CatalogFragrance[]>('/admin/catalog/fragrances').then(r => r.data);

export const getCatalogFragranceTypes = (): Promise<CatalogFragranceType[]> =>
  api.get<CatalogFragranceType[]>('/admin/catalog/fragrance-types').then(r => r.data);

export const getCatalogPackaging = (): Promise<CatalogPackaging[]> =>
  api.get<CatalogPackaging[]>('/admin/catalog/packaging').then(r => r.data);

export const getPackagingSets = (): Promise<CatalogPackagingSet[]> =>
  api.get<CatalogPackagingSet[]>('/admin/catalog/packaging-sets').then(r => r.data);

export const getCatalogGeneratedSkus = (): Promise<CatalogGeneratedSku[]> =>
  api.get<CatalogGeneratedSku[]>('/admin/catalog/generated-skus').then(r => r.data);

export const getCatalogLabels = (): Promise<CatalogLabel[]> =>
  api.get<CatalogLabel[]>('/admin/catalog/labels').then(r => r.data);

export const getCatalogFiles = (): Promise<CatalogFile[]> =>
  api.get<CatalogFile[]>('/admin/catalog/files').then(r => r.data);

export const getCatalogSkuHistory = (skuId?: string): Promise<CatalogSkuHistory[]> =>
  api.get<CatalogSkuHistory[]>('/admin/catalog/sku-history', { params: skuId ? { skuId } : undefined }).then(r => r.data);

// ── Catalog POST functions (T083) ─────────────────────────────────────────────

export const createCatalogSupplier = (data: CreateCatalogSupplierData): Promise<void> =>
  api.post('/admin/catalog/suppliers', data).then(() => undefined);

export const createCatalogLocation = (data: CreateCatalogLocationData): Promise<void> =>
  api.post('/admin/catalog/locations', data).then(() => undefined);

export const createCatalogCategory = (data: CreateCatalogCategoryData): Promise<void> =>
  api.post('/admin/catalog/categories', data).then(() => undefined);

export const createCatalogConcernTag = (data: CreateCatalogConcernTagData): Promise<void> =>
  api.post('/admin/catalog/concern-tags', data).then(() => undefined);

export const createCatalogPackageType = (data: CreateCatalogPackageTypeData): Promise<void> =>
  api.post('/admin/catalog/package-types', data).then(() => undefined);

export const createCatalogBase = (data: CreateCatalogBaseData): Promise<void> =>
  api.post('/admin/catalog/bases', data).then(() => undefined);

export const createCatalogActive = (data: CreateCatalogActiveData): Promise<void> =>
  api.post('/admin/catalog/actives', data).then(() => undefined);

export const createCatalogFragrance = (data: CreateCatalogFragranceData): Promise<void> =>
  api.post('/admin/catalog/fragrances', data).then(() => undefined);

export const createCatalogFragranceType = (data: CreateCatalogFragranceTypeData): Promise<void> =>
  api.post('/admin/catalog/fragrance-types', data).then(() => undefined);

export const createCatalogPackaging = (data: CreateCatalogPackagingData): Promise<void> =>
  api.post('/admin/catalog/packaging', data).then(() => undefined);

export const createPackagingSet = (data: CreateCatalogPackagingSetData): Promise<void> =>
  api.post('/admin/catalog/packaging-sets', data).then(() => undefined);

export const createCatalogGeneratedSku = (data: CreateCatalogGeneratedSkuData): Promise<void> =>
  api.post('/admin/catalog/generated-skus', data).then(() => undefined);

export const createCatalogLabel = (
  data: CreateCatalogLabelData,
): Promise<{ id: string; labelId: string }> =>
  api.post<{ id: string; labelId: string }>('/admin/catalog/labels', data).then(r => r.data);

export const createCatalogFile = (data: CreateCatalogFileData): Promise<void> =>
  api.post('/admin/catalog/files', data).then(() => undefined);

// ── Catalog PUT functions (T084) ─────────────────────────────────────────────

export const updateCatalogSupplier = (id: number, data: UpdateCatalogSupplierData): Promise<void> =>
  api.put(`/admin/catalog/suppliers/${id}`, data).then(() => undefined);

export const updateCatalogLocation = (id: number, data: UpdateCatalogLocationData): Promise<void> =>
  api.put(`/admin/catalog/locations/${id}`, data).then(() => undefined);

export const updateCatalogCategory = (id: number, data: UpdateCatalogCategoryData): Promise<void> =>
  api.put(`/admin/catalog/categories/${id}`, data).then(() => undefined);

export const updateCatalogConcernTag = (id: number, data: UpdateCatalogConcernTagData): Promise<void> =>
  api.put(`/admin/catalog/concern-tags/${id}`, data).then(() => undefined);

export const updateCatalogPackageType = (id: number, data: UpdateCatalogPackageTypeData): Promise<void> =>
  api.put(`/admin/catalog/package-types/${id}`, data).then(() => undefined);

export const updateCatalogBase = (id: number, data: UpdateCatalogBaseData): Promise<void> =>
  api.put(`/admin/catalog/bases/${id}`, data).then(() => undefined);

export const updateCatalogActive = (id: number, data: UpdateCatalogActiveData): Promise<void> =>
  api.put(`/admin/catalog/actives/${id}`, data).then(() => undefined);

export const updateCatalogFragrance = (id: number, data: UpdateCatalogFragranceData): Promise<void> =>
  api.put(`/admin/catalog/fragrances/${id}`, data).then(() => undefined);

export const updateCatalogFragranceType = (id: number, data: UpdateCatalogFragranceTypeData): Promise<void> =>
  api.put(`/admin/catalog/fragrance-types/${id}`, data).then(() => undefined);

export const updateCatalogPackaging = (id: string, data: UpdateCatalogPackagingData): Promise<void> =>
  api.put(`/admin/catalog/packaging/${id}`, data).then(() => undefined);

export const updateCatalogGeneratedSku = (id: string, data: UpdateCatalogGeneratedSkuData): Promise<void> =>
  api.put(`/admin/catalog/generated-skus/${id}`, data).then(() => undefined);

export const updateCatalogLabel = (id: string, data: UpdateCatalogLabelData): Promise<void> =>
  api.put(`/admin/catalog/labels/${id}`, data).then(() => undefined);

export const updateCatalogFile = (id: string, data: UpdateCatalogFileData): Promise<void> =>
  api.put(`/admin/catalog/files/${id}`, data).then(() => undefined);

export const updatePackagingSet = (id: string, data: UpdateCatalogPackagingSetData): Promise<void> =>
  api.put(`/admin/catalog/packaging-sets/${id}`, data).then(() => undefined);

export const getCatalogOrders = (): Promise<CatalogOrder[]> =>
  api.get<CatalogOrder[]>('/admin/catalog/orders').then(r => r.data);

export const createCatalogOrder = (data: CreateCatalogOrderData): Promise<void> =>
  api.post('/admin/catalog/orders', data).then(() => undefined);

export const updateCatalogOrder = (id: string, data: UpdateCatalogOrderData): Promise<void> =>
  api.put(`/admin/catalog/orders/${id}`, data).then(() => undefined);

export const uploadCatalogFile = (
  file: File,
  table?: string,
  itemId?: string,
): Promise<{ id: string; fileName: string; url: string }> => {
  const fd = new FormData();
  fd.append('file', file);
  if (table) fd.append('table', table);
  if (itemId) fd.append('itemId', itemId);
  return api.post('/admin/catalog/files/upload', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then(r => r.data);
};

// ── Catalog order-site visibility toggle (Feature 047) ────────────────────────
// Each PATCHes show_in_order_site for one catalog item. No direct fetch in components.

export const patchCatalogBaseVisibility = (id: number, showInOrderSite: boolean): Promise<void> =>
  api.patch(`/admin/catalog/bases/${id}/visibility`, { showInOrderSite }).then(() => undefined);

export const patchCatalogActiveVisibility = (id: number, showInOrderSite: boolean): Promise<void> =>
  api.patch(`/admin/catalog/actives/${id}/visibility`, { showInOrderSite }).then(() => undefined);

export const patchCatalogFragranceVisibility = (id: number, showInOrderSite: boolean): Promise<void> =>
  api.patch(`/admin/catalog/fragrances/${id}/visibility`, { showInOrderSite }).then(() => undefined);

export const patchCatalogPackagingSetVisibility = (id: string, showInOrderSite: boolean): Promise<void> =>
  api.patch(`/admin/catalog/packaging-sets/${id}/visibility`, { showInOrderSite }).then(() => undefined);

// ── Catalog Import functions (T152) ───────────────────────────────────────────

export interface CatalogImportRequest {
  mode: 'replace' | 'add-or-edit';
  rows: Record<string, string>[];
}

export interface CatalogImportResult {
  inserted: number;
  updated: number;
  deleted: number;
  skipped: number;
}

const importCatalog = (table: string, req: CatalogImportRequest): Promise<CatalogImportResult> =>
  api.post<CatalogImportResult>(`/admin/catalog/${table}/import`, req).then(r => r.data);

export const importCatalogSuppliers     = (req: CatalogImportRequest) => importCatalog('suppliers', req);
export const importCatalogLocations     = (req: CatalogImportRequest) => importCatalog('locations', req);
export const importCatalogCategories    = (req: CatalogImportRequest) => importCatalog('categories', req);
export const importCatalogConcernTags   = (req: CatalogImportRequest) => importCatalog('concern-tags', req);
export const importCatalogPackageTypes  = (req: CatalogImportRequest) => importCatalog('package-types', req);
export const importCatalogBases         = (req: CatalogImportRequest) => importCatalog('bases', req);
export const importCatalogActives       = (req: CatalogImportRequest) => importCatalog('actives', req);
export const importCatalogFragrances    = (req: CatalogImportRequest) => importCatalog('fragrances', req);
export const importCatalogPackaging     = (req: CatalogImportRequest) => importCatalog('packaging', req);
export const importCatalogPackagingSets = (req: CatalogImportRequest) => importCatalog('packaging-sets', req);
export const importCatalogGeneratedSkus = (req: CatalogImportRequest) => importCatalog('generated-skus', req);
export const importCatalogLabels        = (req: CatalogImportRequest) => importCatalog('labels', req);
export const importCatalogFiles         = (req: CatalogImportRequest) => importCatalog('files', req);

// ── Catalog DELETE functions ──────────────────────────────────────────────────

export const deleteCatalogSupplier     = (id: number): Promise<void> =>
  api.delete(`/admin/catalog/suppliers/${id}`).then(() => undefined);
export const deleteCatalogLocation     = (id: number): Promise<void> =>
  api.delete(`/admin/catalog/locations/${id}`).then(() => undefined);
export const deleteCatalogCategory     = (id: number): Promise<void> =>
  api.delete(`/admin/catalog/categories/${id}`).then(() => undefined);
export const deleteCatalogConcernTag   = (id: number): Promise<void> =>
  api.delete(`/admin/catalog/concern-tags/${id}`).then(() => undefined);
export const deleteCatalogPackageType  = (id: number): Promise<void> =>
  api.delete(`/admin/catalog/package-types/${id}`).then(() => undefined);
export const deleteCatalogBase         = (id: number): Promise<void> =>
  api.delete(`/admin/catalog/bases/${id}`).then(() => undefined);
export const deleteCatalogActive       = (id: number): Promise<void> =>
  api.delete(`/admin/catalog/actives/${id}`).then(() => undefined);
export const deleteCatalogFragrance    = (id: number): Promise<void> =>
  api.delete(`/admin/catalog/fragrances/${id}`).then(() => undefined);
export const deleteCatalogFragranceType = (id: number): Promise<void> =>
  api.delete(`/admin/catalog/fragrance-types/${id}`).then(() => undefined);
export const deleteCatalogPackaging    = (id: string): Promise<void> =>
  api.delete(`/admin/catalog/packaging/${id}`).then(() => undefined);
export const deletePackagingSet        = (id: string): Promise<void> =>
  api.delete(`/admin/catalog/packaging-sets/${id}`).then(() => undefined);
export const deleteCatalogGeneratedSku = (id: string): Promise<void> =>
  api.delete(`/admin/catalog/generated-skus/${id}`).then(() => undefined);
export const deleteCatalogLabel        = (id: string): Promise<void> =>
  api.delete(`/admin/catalog/labels/${id}`).then(() => undefined);

export const uploadLabelFile = (labelId: string, file: File, fileTitle?: string): Promise<LabelFileResponse> => {
  const fd = new FormData();
  fd.append('file', file);
  if (fileTitle) fd.append('fileTitle', fileTitle);
  return api.post<LabelFileResponse>(`/admin/catalog/labels/${labelId}/files`, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then(r => r.data);
};

export const deleteLabelFile = (labelId: string, fileId: string): Promise<void> =>
  api.delete(`/admin/catalog/labels/${labelId}/files/${fileId}`).then(() => undefined);
export const deleteCatalogFile         = (id: string): Promise<void> =>
  api.delete(`/admin/catalog/files/${id}`).then(() => undefined);
export const deleteCatalogSkuHistory   = (id: number): Promise<void> =>
  api.delete(`/admin/catalog/sku-history/${id}`).then(() => undefined);

// ── Dashboard Task Summary Extensions (T117) ──────────────────────────────────

export const getTaskSummary = (): Promise<any> =>
    api.get('/tasks-summary').then(r => r.data);
export const getComplianceStatus = (skuId: string): Promise<import('@/types/catalog').ComplianceStatusRecord> =>
  api.get(`/admin/catalog/compliance-status/${skuId}`).then(r => r.data);

export const getNextExternalId = (table: string): Promise<{ nextId: string }> =>
  api.get(`/admin/catalog/${encodeURIComponent(table)}/next-id`).then(r => r.data);

export const updateComplianceStatus = (skuId: string, data: import('@/types/catalog').UpdateComplianceStatusData): Promise<void> =>
  api.put(`/admin/catalog/compliance-status/${skuId}`, data).then(() => undefined);
